/**
 * Admin report page (admin.brokeornot.com) — quick-260909-k4v.
 *
 * One screen, one table: registered users and how many money-in/money-out occurrences each
 * has in the last 30 days and the next 30 days.
 *
 * SIGN-IN IS GOOGLE-ONLY. There is deliberately no email/password form and no sign-up link,
 * so this does NOT use Amplify's <Authenticator> — that component always renders the
 * password form, and hiding it with CSS would be a lie that breaks on the next Amplify
 * release. Instead the gate is a small explicit state machine over ensureValidSession()
 * plus a single signInWithRedirect({ provider: 'Google' }) button. Dropping
 * @aws-amplify/ui-react also takes its ~330 KB stylesheet out of this bundle.
 *
 * Worth being precise about what this does and does not change: it removes the password
 * UI, not password AUTHENTICATION. The Cognito app client still accepts SRP, so someone
 * could in principle get a token with a password outside this page. It does not matter
 * here, because the report's gate is an email allowlist checked server-side and the only
 * allowed address (isuru137@gmail.com) is an EXTERNAL_PROVIDER account with no password to
 * use. If a password-holding address were ever added to ADMIN_EMAILS, that would stop
 * being true — the client's ExplicitAuthFlows would need tightening as well.
 *
 * Styling mirrors app.brokeornot.com rather than inventing a look: the same sticky
 * white/blur header with the brand lockup, the same slate-50 ground, the same Inter type
 * and blue-500 accent — inherited from ../src/index.css and ../tailwind.config.js, which
 * this build shares with the main app.
 *
 * Access is enforced by the API, not by this component. A 403 renders an explicit refusal
 * screen, but the gate that matters lives in lambda/admin-stats/handler.ts — anyone with a
 * pool account can reach this page, and only a listed admin gets data back from it.
 */
import { useCallback, useEffect, useState } from 'react';
import { signInWithRedirect, signOut, fetchAuthSession } from 'aws-amplify/auth';
import { Hub } from 'aws-amplify/utils';
import { ensureValidSession } from '@/lib/auth';
import { BrandLogo } from '@/components/BrandLogo';
import { useAdminStatsQuery, AdminAccessDeniedError } from './adminStatsApi';
import { StatsTable, shortDate, platformLabel } from './UserStatsTable';

/** Google's four-colour "G". Inline so it adds no request and works offline — same asset
 *  the main app's native sign-in button uses. */
function GoogleMark({ className }: { className?: string }) {
  return (
    <svg className={className} viewBox="0 0 48 48" aria-hidden="true">
      <path
        fill="#EA4335"
        d="M24 9.5c3.54 0 6.71 1.22 9.21 3.6l6.85-6.85C35.9 2.38 30.47 0 24 0 14.62 0 6.51 5.38 2.56 13.22l7.98 6.19C12.43 13.72 17.74 9.5 24 9.5z"
      />
      <path
        fill="#4285F4"
        d="M46.98 24.55c0-1.57-.15-3.09-.38-4.55H24v9.02h12.94c-.58 2.96-2.26 5.48-4.78 7.18l7.73 6c4.51-4.18 7.09-10.36 7.09-17.65z"
      />
      <path
        fill="#FBBC05"
        d="M10.53 28.59c-.48-1.45-.76-2.99-.76-4.59s.27-3.14.76-4.59l-7.98-6.19C.92 16.46 0 20.12 0 24c0 3.88.92 7.54 2.56 10.78l7.97-6.19z"
      />
      <path
        fill="#34A853"
        d="M24 48c6.48 0 11.93-2.13 15.89-5.81l-7.73-6c-2.15 1.45-4.92 2.3-8.16 2.3-6.26 0-11.57-4.22-13.47-9.91l-7.98 6.19C6.51 42.62 14.62 48 24 48z"
      />
    </svg>
  );
}

/**
 * The signed-in address, read from the ID token so the header can show who is looking.
 *
 * Purely cosmetic — the report's access decision is made server-side from Cognito
 * (AdminGetUser), never from this claim. A caller could put anything in a client-side token
 * copy and it would change nothing about what the API returns.
 */
function useSignedInEmail(): string | undefined {
  const [email, setEmail] = useState<string | undefined>();

  useEffect(() => {
    let cancelled = false;
    void (async () => {
      try {
        const session = await fetchAuthSession();
        const claim = session.tokens?.idToken?.payload?.email;
        if (!cancelled) setEmail(typeof claim === 'string' ? claim : undefined);
      } catch {
        if (!cancelled) setEmail(undefined);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, []);

  return email;
}

/**
 * Normalise the address bar to the site root once signed in.
 *
 * Amplify's own success path already strips ?code=&state= (clearHistory() inside
 * completeOAuthFlow), but it replaces the URL with the redirect URI — which is /login — so
 * a signed-in admin would otherwise sit on /login forever. This also mops up the failure
 * shapes (a lingering ?error=) that Amplify leaves behind.
 *
 * Runs from a component that is mounted only AFTER a session exists, and that placement is
 * load-bearing: doing this at module scope in main.tsx, as an earlier version did, broke
 * Google sign-in completely. attemptCompleteOAuthFlow awaits
 * oAuthStore.loadOAuthInFlight() BEFORE reading window.location.href, so any synchronous
 * rewrite at load time wins that race and hands Amplify a URL with no authorization code
 * in it. By the time this runs the code has already been redeemed.
 *
 * replaceState rather than pushState so no code-bearing URL is left in history, where a
 * Back press would try to redeem an already-consumed single-use code.
 */
function useTidyUrl() {
  useEffect(() => {
    if (window.location.pathname !== '/' || window.location.search !== '') {
      window.history.replaceState({}, '', '/');
    }
  }, []);
}

/** Full-height slate ground shared by the sign-in and loading screens. */
const CENTRED_PAGE = 'flex min-h-screen items-center justify-center bg-slate-50 p-6';

/** Brand lockup above the sign-in card, matching app.brokeornot.com/login. */
function BrandHeader() {
  return (
    <div className="mb-6 flex flex-col items-center text-center">
      <div className="mb-3 flex items-center gap-2">
        <span className="flex h-10 w-10 items-center justify-center rounded-xl bg-blue-500 text-white">
          <BrandLogo className="h-7 w-7" />
        </span>
        <span className="text-2xl font-semibold tracking-tight text-slate-900">Broke or Not</span>
      </div>
      <p className="text-sm text-slate-500">Admin — sign in with an authorised account.</p>
    </div>
  );
}

/** Google-only sign-in screen. No password fields, no sign-up. */
function SignInScreen() {
  const [pending, setPending] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const handleSignIn = useCallback(() => {
    setError(null);
    setPending(true);
    // Navigates the window to the Cognito Hosted UI, so on the happy path nothing after
    // this resolves. It can still reject — most usefully UserAlreadyAuthenticatedException
    // when stale tokens survive — so failures have to be surfaced rather than swallowed,
    // otherwise the button just appears to do nothing.
    signInWithRedirect({ provider: 'Google' }).catch((err: unknown) => {
      setPending(false);
      setError(
        err instanceof Error && err.name === 'UserAlreadyAuthenticatedException'
          ? 'You are already signed in — reload the page.'
          : 'Could not start Google sign-in. Please try again.'
      );
    });
  }, []);

  return (
    <main className={CENTRED_PAGE}>
      <div className="flex w-full max-w-sm flex-col items-center">
        <BrandHeader />

        <div className="w-full rounded-xl border border-slate-200 bg-white p-6 shadow-sm">
          <button
            type="button"
            onClick={handleSignIn}
            disabled={pending}
            className="flex w-full items-center justify-center gap-3 rounded-md border border-slate-300 bg-white px-4 py-2.5 text-sm font-semibold text-slate-700 transition-colors hover:bg-slate-50 disabled:cursor-not-allowed disabled:opacity-60"
          >
            <GoogleMark className="h-4 w-4 shrink-0" />
            {pending ? 'Redirecting…' : 'Sign in with Google'}
          </button>

          {error && (
            <p role="alert" className="mt-3 text-center text-xs text-red-600">
              {error}
            </p>
          )}

          <p className="mt-4 text-center text-xs text-slate-400">
            Access is restricted to authorised accounts.
          </p>
        </div>
      </div>
    </main>
  );
}

/** Shown while the initial session check runs — including the OAuth code exchange, which
 *  ensureValidSession() waits on, so this is what a user sees on the way back from Google. */
function LoadingScreen({ label }: { label: string }) {
  return (
    <main className={CENTRED_PAGE}>
      <div className="flex flex-col items-center">
        <BrandHeader />
        <p className="text-sm text-slate-500">{label}</p>
      </div>
    </main>
  );
}

/**
 * The report itself. Rendered only once a session is confirmed, which is what makes the
 * email lookup reliable — reading it any earlier returns undefined, because at that point
 * sign-in has not happened yet.
 */
function AdminReport() {
  const { data, isPending, error, refetch, isFetching } = useAdminStatsQuery();
  const email = useSignedInEmail();
  useTidyUrl();

  const handleSignOut = useCallback(() => {
    void signOut();
  }, []);

  return (
    <div className="min-h-screen bg-slate-50">
      {/* Same header treatment as the main app's _app.tsx chrome. */}
      <header className="sticky top-0 z-10 border-b border-slate-200 bg-white/80 backdrop-blur">
        <div className="mx-auto flex max-w-6xl items-center justify-between gap-3 px-4 py-3">
          <div className="flex min-w-0 items-center gap-2">
            <span className="flex h-8 w-8 shrink-0 items-center justify-center rounded-lg bg-blue-500 text-white">
              <BrandLogo className="h-5 w-5" />
            </span>
            <span className="truncate text-base font-semibold tracking-tight text-slate-900">
              Broke or Not
            </span>
            <span className="rounded bg-slate-100 px-1.5 py-0.5 text-xs font-medium text-slate-500">
              Admin
            </span>
          </div>
          <div className="flex items-center gap-3">
            {email && <span className="hidden text-sm text-slate-500 sm:inline">{email}</span>}
            <button
              type="button"
              onClick={handleSignOut}
              className="rounded-md border border-slate-200 px-3 py-1.5 text-sm text-slate-700 transition-colors hover:bg-slate-100"
            >
              Sign out
            </button>
          </div>
        </div>
      </header>

      <main className="mx-auto max-w-6xl px-4 py-8">
        {error instanceof AdminAccessDeniedError ? (
          <div className="rounded-xl border border-slate-200 bg-white p-8 text-center shadow-sm">
            <h1 className="text-lg font-semibold text-slate-900">Not authorised</h1>
            <p className="mx-auto mt-2 max-w-md text-sm text-slate-500">
              {email ? <span className="font-medium">{email}</span> : 'This account'} cannot view
              the admin report. Sign in with an authorised account.
            </p>
            <button
              type="button"
              onClick={handleSignOut}
              className="mt-5 rounded-md bg-blue-500 px-4 py-2 text-sm font-medium text-white transition-colors hover:bg-blue-600"
            >
              Sign out
            </button>
          </div>
        ) : error ? (
          <div className="rounded-xl border border-slate-200 bg-white p-8 text-center shadow-sm">
            <h1 className="text-lg font-semibold text-slate-900">Could not load the report</h1>
            <p className="mt-2 text-sm text-slate-500">{error.message}</p>
            <button
              type="button"
              onClick={() => void refetch()}
              className="mt-5 rounded-md bg-blue-500 px-4 py-2 text-sm font-medium text-white transition-colors hover:bg-blue-600"
            >
              Try again
            </button>
          </div>
        ) : (
          <>
            <div className="mb-6 flex flex-wrap items-end justify-between gap-4">
              <div>
                <h1 className="text-2xl font-semibold tracking-tight text-slate-900">
                  Registered users
                </h1>
                <p className="mt-1 text-sm text-slate-500">
                  {isPending ? (
                    'Loading…'
                  ) : (
                    <>
                      <span className="font-semibold text-slate-900">{data?.userCount ?? 0}</span>{' '}
                      {data?.userCount === 1 ? 'user' : 'users'}
                      {data && data.excludedCount > 0 && (
                        <> · {data.excludedCount} test/demo account(s) excluded</>
                      )}
                      {data?.platformCounts &&
                        (['ios', 'android', 'web', 'unknown'] as const).map((p) => (
                          <span key={p}>
                            {' · '}
                            {platformLabel(p)}{' '}
                            <span className="font-semibold text-slate-900">
                              {data.platformCounts?.[p] ?? 0}
                            </span>
                          </span>
                        ))}
                    </>
                  )}
                </p>
              </div>
              <button
                type="button"
                onClick={() => void refetch()}
                disabled={isFetching}
                className="rounded-md border border-slate-200 bg-white px-3 py-1.5 text-sm text-slate-700 transition-colors hover:bg-slate-100 disabled:opacity-50"
              >
                {isFetching ? 'Refreshing…' : 'Refresh'}
              </button>
            </div>

            <div className="overflow-hidden rounded-xl border border-slate-200 bg-white shadow-sm">
              {isPending ? (
                <p className="px-3 py-12 text-center text-sm text-slate-500">Loading report…</p>
              ) : (
                <StatsTable users={data?.users ?? []} />
              )}
            </div>

            {data && (
              /* The exact windows the server counted, so a number can always be traced back
                 to a date range rather than to "30 days" as a figure of speech. */
              <p className="mt-4 text-xs text-slate-400">
                Counts are calendar occurrences, so a recurring entry is counted once per time
                it lands in the window. Last 30 days: {shortDate(data.windows.last30.start)} –{' '}
                {shortDate(data.windows.last30.end)}. Next 30 days:{' '}
                {shortDate(data.windows.next30.start)} – {shortDate(data.windows.next30.end)}.
                Generated {new Date(data.generatedAt).toLocaleString()}.
              </p>
            )}
          </>
        )}
      </main>
    </div>
  );
}

/**
 * Sign-in gate.
 *
 * ensureValidSession() is reused from the main app rather than reimplemented: it confirms
 * the session can actually produce a token (forcing a refresh when needed) instead of just
 * decoding cached ones, and it already handles the two OAuth callback edge cases that cost
 * a debug session to find — a failed exchange leaving ?code=&state= stranded in the URL,
 * and a bare signOut() on the callback page firing an unwanted federated logout redirect
 * for a returning Google user. It also waits on an in-flight exchange, which is what makes
 * the single check below correct on the way back from Google.
 *
 * The Hub listener covers the transitions the initial check cannot see: signing out (so the
 * page returns to the sign-in screen without a reload) and a sign-in completing after this
 * component has already settled.
 */
export function AdminApp() {
  const [status, setStatus] = useState<'checking' | 'signed-out' | 'signed-in'>('checking');

  useEffect(() => {
    let cancelled = false;
    void (async () => {
      const valid = await ensureValidSession();
      if (!cancelled) setStatus(valid ? 'signed-in' : 'signed-out');
    })();
    return () => {
      cancelled = true;
    };
  }, []);

  useEffect(
    () =>
      Hub.listen('auth', ({ payload }) => {
        if (payload.event === 'signedIn') setStatus('signed-in');
        // signInWithRedirect_failure fires when the code exchange fails (expired or reused
        // code, network blip). Without it the page would sit on "Signing you in…" forever
        // instead of offering the button again.
        if (payload.event === 'signedOut' || payload.event === 'signInWithRedirect_failure') {
          setStatus('signed-out');
        }
      }),
    []
  );

  if (status === 'checking') {
    // On the way back from Google this is the OAuth exchange, not a session lookup, so the
    // wording covers the case a user actually waits on.
    return <LoadingScreen label="Signing you in…" />;
  }

  return status === 'signed-in' ? <AdminReport /> : <SignInScreen />;
}
